import React, { useEffect, useState, useCallback } from 'react';
import { Flame, AlertTriangle, Clock, RefreshCw, RotateCcw, HeartPulse } from 'lucide-react';
import api from '../lib/api';

const CATEGORIES = [
  { key: 'hot_leads', label: 'Hot / High-Intent Buyers', icon: Flame,
    hint: 'Buyers showing strong or high purchase intent right now.' },
  { key: 'open_objections', label: 'Open Objections', icon: AlertTriangle,
    hint: 'Buyers with an unresolved concern (price, MOQ, etc.).' },
  { key: 'followups_due', label: 'Follow-ups Due', icon: Clock,
    hint: 'Planned follow-up suggestions — nothing is auto-sent.' },
  { key: 'reorder_candidates', label: 'Reorder Candidates', icon: RotateCcw,
    hint: 'Past buyers who look ready to reorder.' },
  { key: 'reactivation_candidates', label: 'Reactivation Candidates', icon: HeartPulse,
    hint: 'Dormant buyers worth re-engaging.' },
];

const TREND_STYLES = {
  ADVANCING: 'bg-green-100 text-green-700',
  STEADY: 'bg-slate-100 text-slate-600',
  STALLED: 'bg-amber-100 text-amber-700',
  COOLING: 'bg-orange-100 text-orange-700',
  LOST: 'bg-red-100 text-red-700',
};

const PRIORITY_STYLES = {
  CRITICAL: 'bg-red-100 text-red-700',
  HIGH: 'bg-orange-100 text-orange-700',
  MEDIUM: 'bg-amber-100 text-amber-700',
  LOW: 'bg-slate-100 text-slate-600',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
  catch { return '—'; }
}

function OppRow({ op }) {
  const name = op.buyer_name || op.buyer_phone || op.buyer_key?.slice(0, 12) || 'Buyer';
  const pr = (op.nba_priority || 'NONE').toUpperCase();
  return (
    <div
      data-testid={`opp-row-${op.buyer_key}`}
      className="flex flex-col gap-1 rounded-lg border border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-slate-900" data-testid="opp-buyer-name">{name}</span>
          {pr !== 'NONE' && (
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${PRIORITY_STYLES[pr] || PRIORITY_STYLES.LOW}`}>
              {pr}
            </span>
          )}
          {op.trend?.trend && (
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${TREND_STYLES[op.trend.trend] || 'bg-slate-100 text-slate-600'}`}
              title={op.trend.evidence?.reason || ''} data-testid="opp-trend">
              {op.trend.trend}
            </span>
          )}
        </div>
        <div className="mt-0.5 truncate text-sm text-slate-500">
          {op.current_intent && <span className="mr-2">Intent: {op.current_intent}{op.intent_strength ? ` (${op.intent_strength})` : ''}</span>}
          {op.objection_type && op.objection_type !== 'NONE' && <span className="mr-2">Objection: {op.objection_type}</span>}
          {op.next_best_action && <span className="mr-2">Next: {op.next_best_action}</span>}
          {Array.isArray(op.products_of_interest) && op.products_of_interest.length > 0 && (
            <span className="mr-2">Products: {op.products_of_interest.slice(0, 3).join(', ')}</span>
          )}
        </div>
      </div>
      <div className="shrink-0 text-right text-xs text-slate-400">
        <div>Last active {fmtDate(op.last_activity_at)}</div>
        {op.follow_up_target_at && <div>Due {fmtDate(op.follow_up_target_at)}</div>}
      </div>
    </div>
  );
}

export default function SellerOpportunities() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/seller/opportunities');
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load opportunities.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = data?.counts || {};
  const cats = data?.categories || {};

  return (
    <div className="space-y-6" data-testid="seller-opportunities-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Opportunities</h1>
          <p className="mt-1 text-sm text-slate-500">
            Your live Sales Intelligence — who to focus on next. Read-only; nothing is sent automatically.
          </p>
        </div>
        <button
          data-testid="opps-refresh-btn"
          onClick={load}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {error && (
        <div data-testid="opps-error" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="text-sm text-slate-400" data-testid="opps-loading">Loading opportunities…</div>
      )}

      {data && (
        <div className="space-y-6">
          {CATEGORIES.map(({ key, label, icon: Icon, hint }) => {
            const items = cats[key] || [];
            return (
              <section key={key} data-testid={`opp-section-${key}`} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                <div className="mb-3 flex items-center gap-2">
                  <Icon className="h-5 w-5 text-slate-600" />
                  <h2 className="text-base font-semibold text-slate-800">{label}</h2>
                  <span
                    data-testid={`opp-count-${key}`}
                    className="ml-1 rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700"
                  >
                    {counts[key] ?? items.length}
                  </span>
                </div>
                <p className="mb-3 text-xs text-slate-400">{hint}</p>
                {items.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-400" data-testid={`opp-empty-${key}`}>
                    Nothing here right now.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {items.map((op) => <OppRow key={op.buyer_key} op={op} />)}
                  </div>
                )}
              </section>
            );
          })}
          {data.capped_at && (
            <p className="text-center text-xs text-slate-400">
              Showing up to {data.capped_at} per category.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
